import { Thermometer, Droplet, Eye, Sun, Haze, ArrowUp, ArrowDown } from 'lucide-react'
import { useEaseTo } from '@/components/hooks/useEaseTo'
import { Barometer } from './Barometer'
import { aqiCategory, uvDescriptor, dewPointFeel, visibilityFeel } from '@/lib/openmeteo'
import { spcMeta, type SpcRisk } from '@/lib/spc'
import type { PressureTrend } from '@/lib/nws/client'
import './chips.css'

// Per-metric card accent = that chip's icon colour. Drives the frosted card's faint glow +
// border (see .chip in chips.css). Blue is the default family; UV is amber; AQI + Severe are
// data-driven (their category/ring colour, alpha-matched via color-mix).
const ACCENT_BLUE = 'rgba(59, 130, 246, 0.2)' // Feels / Humidity / Wind / Pressure / Visibility
const ACCENT_AMBER = 'rgba(255, 184, 58, 0.22)' // UV index

/**
 * 8 compact condition tiles (4×2), each with a CSS-animated icon (all gated under
 * prefers-reduced-motion). Values count up on mount/refresh (useEaseTo).
 */
export function ConditionChips(p: {
  feels: number | null
  feelsDiff: number | null
  humidity: number | null
  dewPointF: number | null
  wind: number | null
  windDir: string
  pressure: number | null
  pressureTrend: PressureTrend
  aqi: number | null
  uv: number | null
  visibilityMi: number | null
  spc: SpcRisk | null
}) {
  return (
    <div className="cond-chips">
      <FeelsChip feels={p.feels} feelsDiff={p.feelsDiff} />
      <HumidityChip humidity={p.humidity} dewPointF={p.dewPointF} />
      <WindChip wind={p.wind} windDir={p.windDir} />
      <PressureChip pressure={p.pressure} trend={p.pressureTrend} />
      <AqiChip aqi={p.aqi} />
      <UvChip uv={p.uv} />
      <VisibilityChip mi={p.visibilityMi} />
      <SpcChip spc={p.spc} />
    </div>
  )
}

function Chip({
  icon,
  value,
  label,
  sub,
  accent,
}: {
  icon: React.ReactNode
  value: React.ReactNode
  label: string
  sub: React.ReactNode
  /** Per-metric accent (the chip's icon colour) — drives the card's faint glow + border. */
  accent: string
}) {
  return (
    <div className="chip" style={{ '--chip-accent': accent } as React.CSSProperties}>
      <span className="chip__ic">{icon}</span>
      {value !== '' && value != null && <span className="chip__val num">{value}</span>}
      <span className="chip__meta">
        <span className="chip__lbl">{label}</span>
        <span className="chip__sub">{sub}</span>
      </span>
    </div>
  )
}

function FeelsChip({ feels, feelsDiff }: { feels: number | null; feelsDiff: number | null }) {
  const v = useEaseTo(feels == null ? null : Math.round(feels))
  const diff = useEaseTo(feelsDiff)
  const warm = (feelsDiff ?? 0) > 0
  const mag = diff == null ? 0 : Math.abs(Math.round(diff))
  const sub =
    feelsDiff == null ? (
      'current'
    ) : feelsDiff === 0 ? (
      'same'
    ) : (
      <span className="chip__sub--amber">
        {warm ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
        <span className="num">{mag}°</span> {warm ? 'warmer' : 'cooler'}
      </span>
    )
  return (
    <Chip icon={<Thermometer size={19} className="ic-pulse" />} value={v == null ? '—' : `${Math.round(v)}°`} label="Feels" sub={sub} accent={ACCENT_BLUE} />
  )
}

function HumidityChip({ humidity, dewPointF }: { humidity: number | null; dewPointF: number | null }) {
  const v = useEaseTo(humidity == null ? null : Math.round(humidity))
  // No "·" here (unlike the other sublines): "dp 73° muggy" needs every px to fit the chip width.
  const sub = dewPointF == null ? 'relative' : `dp ${Math.round(dewPointF)}° ${dewPointFeel(dewPointF)}`
  return <Chip icon={<Droplet size={19} className="ic-bob" />} value={v == null ? '—' : `${Math.round(v)}%`} label="Humidity" sub={sub} accent={ACCENT_BLUE} />
}

/** Three-stroke Tabler-wind glyph whose strokes draw themselves in, staggered + looping. */
function WindIcon() {
  return (
    <svg className="windic" width="34" height="30" viewBox="0 0 30 26" fill="none" aria-hidden="true">
      <path className="wp" pathLength={100} style={{ animation: 'drawwind 2.6s ease-in-out infinite 0s' }} d="M4 9 H12 a2.7 2.7 0 1 0 -2.6 -3.4" />
      <path className="wp" pathLength={100} style={{ animation: 'drawwind 2.6s ease-in-out infinite .3s' }} d="M3 13.5 H19 a2.7 2.7 0 1 1 -2.6 3.4" />
      <path className="wp" pathLength={100} style={{ animation: 'drawwind 2.6s ease-in-out infinite .6s' }} d="M5 18 H11 a2.7 2.7 0 1 1 -2.6 3.4" />
    </svg>
  )
}

function WindChip({ wind, windDir }: { wind: number | null; windDir: string }) {
  const noData = wind == null
  const calm = !noData && wind < 0.5
  const v = useEaseTo(noData ? null : Math.round(wind))
  return (
    <Chip
      icon={<WindIcon />}
      value={noData ? '—' : calm ? '0' : v == null ? '—' : Math.round(v)}
      label="Wind"
      sub={noData ? 'mph' : calm ? 'calm' : windDir ? `mph ${windDir}` : 'mph'}
      accent={ACCENT_BLUE}
    />
  )
}

function PressureChip({ pressure, trend }: { pressure: number | null; trend: PressureTrend }) {
  const v = useEaseTo(pressure == null ? null : Math.round(pressure * 100))
  return (
    <Chip
      icon={<Barometer pressure={pressure} trend={trend} size={32} />}
      value={v == null ? '—' : (v / 100).toFixed(2)}
      label="Pressure"
      sub={`inHg · ${trend}`}
      accent={ACCENT_BLUE}
    />
  )
}

function AqiChip({ aqi }: { aqi: number | null }) {
  const v = useEaseTo(aqi)
  const meta = aqiCategory(aqi)
  return (
    <Chip
      icon={<Haze size={19} className="ic-pulse" />}
      value={v == null ? '—' : Math.round(v)}
      label="Air quality"
      sub={
        <span className="chip__sub--dot" style={{ '--dc': meta.color } as React.CSSProperties}>
          <span className="chip__dot" />
          {meta.label}
        </span>
      }
      accent={`color-mix(in srgb, ${meta.color} 20%, transparent)`}
    />
  )
}

function UvChip({ uv }: { uv: number | null }) {
  const v = useEaseTo(uv == null ? null : Math.round(uv))
  return (
    <Chip icon={<Sun size={19} className="ic-spin" />} value={v == null ? '—' : Math.round(v)} label="UV index" sub={uvDescriptor(uv)} accent={ACCENT_AMBER} />
  )
}

function VisibilityChip({ mi }: { mi: number | null }) {
  const v = useEaseTo(mi == null ? null : Math.round(mi))
  return (
    <Chip
      icon={<Eye size={19} className="ic-blink" />}
      value={v == null ? '—' : Math.round(v)}
      label="Visibility"
      sub={mi == null ? 'mi' : `mi · ${visibilityFeel(mi)}`}
      accent={ACCENT_BLUE}
    />
  )
}

function SpcChip({ spc }: { spc: SpcRisk | null }) {
  const meta = spcMeta(spc?.label ?? 'NONE')
  const active = meta.num > 0
  // Surface when the shown risk is a fallback outlook (Day-1 expired → Day-2/3).
  const dayTag = spc && spc.day > 1 ? ` · D${spc.day}` : ''
  return (
    <Chip
      icon={
        <span
          className={`spc-ring${active ? ' is-active' : ''}`}
          style={{ '--rc': meta.color } as React.CSSProperties}
        >
          <span className="spc-ring__num num">{meta.num}</span>
        </span>
      }
      value=""
      label="Severe risk"
      sub={
        <span style={{ color: active ? meta.color : 'var(--text-3)' }}>
          {meta.word}
          {dayTag}
        </span>
      }
      accent={`color-mix(in srgb, ${meta.color} 26%, transparent)`}
    />
  )
}
